import { useQuery } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import { useOrg } from '@/admin/hooks/use-org'
import type { ExternalOrganisationRole } from '@/admin/types/organisations'
import { usePermissions } from '@/admin/hooks/use-permissions'

/**
 * GET /organisations/{orgUuid}/external-roles/
 *
 * Returns the tenant's external (partner) roles, each annotated with a
 * `partnership_count` - the number of top-level partnerships currently
 * using that role. Used to render the role-filter pill strip on the
 * partners list page.
 */
export function useExternalRoles() {
  const { orgUuid } = useOrg()
  // ExternalRoleListCreateView enforces `roles.manage`, which is on
  // NEVER_EXTERNAL_ASSIGNABLE_CODENAMES - no partner session can ever hold it,
  // so for a dealer this request was guaranteed to 403 on every page load.
  const { can } = usePermissions()
  return useQuery<ExternalOrganisationRole[]>({
    queryKey: ['external-roles', orgUuid],
    queryFn: () =>
      api
        .get(`/organisations/${orgUuid}/external-roles/`)
        .then((r) => {
          // Endpoint was migrated to StandardListAPIView so the response now
          // has the DRF-paginated shape `{count, next, previous, results}`.
          // Fall back to `.result` (legacy envelope) and the bare body so this
          // hook keeps working through the rollout.
          const data = r.data
          if (Array.isArray(data)) return data as ExternalOrganisationRole[]
          if (Array.isArray(data?.results)) return data.results as ExternalOrganisationRole[]
          if (Array.isArray(data?.result)) return data.result as ExternalOrganisationRole[]
          return [] as ExternalOrganisationRole[]
        }),
    enabled: !!orgUuid && can('roles.manage'),
  })
}
